import React, { useState, useRef, useEffect } from 'react';
import {
  X, Edit, Plus, CheckCircle, Circle, Clock, FileText,
  User, Calendar, Star, Link as LinkIcon, Upload, MessageSquare,
  Play, AlertCircle, Trash2
} from 'lucide-react';
import { useProjectsStore, Task, TaskAttachment } from '../../../store/projectsStore';
import { useAuthStore } from '../../../store/authStore';
import { useTeamStore } from '../../../store/teamStore';
import TaskAssignmentModal from './TaskAssignmentModal';
import AssetLibraryPicker from './AssetLibraryPicker';

interface TaskDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  task: Task;
  projectId: string;
}

const TaskDetailModal: React.FC<TaskDetailModalProps> = ({ isOpen, onClose, task, projectId }) => {
  const { updateTask, deleteTask, createComment, uploadFile, tasks, comments, fetchComments } = useProjectsStore();
  const { user } = useAuthStore();
  const { teamMembers, fetchTeamMembers } = useTeamStore();

  const [isEditing, setIsEditing] = useState(false);
  const [newSubtask, setNewSubtask] = useState('');
  const [newComment, setNewComment] = useState('');
  const [timeEntryHours, setTimeEntryHours] = useState('');
  const [timeEntryDesc, setTimeEntryDesc] = useState('');
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [showAssetPicker, setShowAssetPicker] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Get latest task data from store
  const currentTask = tasks.find(t => t.id === task.id) || task;
  const [editedTitle, setEditedTitle] = useState(currentTask.title);
  const [editedDescription, setEditedDescription] = useState(currentTask.description || '');
  const [editedDueDate, setEditedDueDate] = useState(currentTask.dueDate || '');

  // Update local state when task changes
  useEffect(() => {
    setEditedTitle(currentTask.title);
    setEditedDescription(currentTask.description || '');
    setEditedDueDate(currentTask.dueDate || '');
  }, [currentTask.title, currentTask.description, currentTask.dueDate]);

  // Fetch team members to display names
  useEffect(() => {
    fetchTeamMembers();
    fetchComments(projectId);
  }, [fetchTeamMembers, fetchComments, projectId]);

  if (!isOpen) return null;

  const formatDate = (dateString: string | undefined): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getAssignedUserName = (userId: string | undefined) => {
    if (!userId) return 'Unassigned';
    const member = teamMembers.find(m => m.id === userId);
    return member ? member.name : 'Unknown User';
  };


  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case 'urgent': return <Star className="w-4 h-4 text-red-500 fill-red-500" />;
      case 'high': return <Star className="w-4 h-4 text-orange-500 fill-orange-500" />;
      case 'medium': return <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />;
      default: return <Star className="w-4 h-4 text-gray-400" />;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'todo': return <Circle className="w-4 h-4 text-gray-400" />;
      case 'in_progress': return <Clock className="w-4 h-4 text-primary-500" />;
      case 'in_review': return <FileText className="w-4 h-4 text-yellow-500" />;
      case 'blocked': return <AlertCircle className="w-4 h-4 text-red-500" />;
      case 'done': return <CheckCircle className="w-4 h-4 text-green-500" />;
      default: return <Circle className="w-4 h-4" />;
    }
  };

  const handleSave = async () => {
    await updateTask({
      id: currentTask.id,
      title: editedTitle,
      description: editedDescription,
      ...(editedDueDate && { dueDate: editedDueDate }),
    });
    setIsEditing(false);
  };

  const handleStatusChange = async (newStatus: Task['status']) => {
    await updateTask({ id: currentTask.id, status: newStatus });
  };

  const handlePriorityChange = async (newPriority: Task['priority']) => {
    await updateTask({ id: currentTask.id, priority: newPriority });
  };

  const handleAddSubtask = async () => {
    if (!newSubtask.trim()) return;
    const currentSubtasks = currentTask.subtasks || [];

    // Sanitize subtasks to remove _id and ensure clean object structure
    const sanitizedSubtasks = currentSubtasks.map(st => ({
      title: st.title,
      completed: st.completed
    }));

    await updateTask({
      id: currentTask.id,
      subtasks: [...sanitizedSubtasks, { title: newSubtask, completed: false }],
    });
    setNewSubtask('');
  };

  const handleToggleSubtask = async (index: number) => {
    const currentSubtasks = currentTask.subtasks || [];

    // Sanitize subtasks and toggle the target one
    const updatedSubtasks = currentSubtasks.map((st, i) => ({
      title: st.title,
      completed: i === index ? !st.completed : st.completed
    }));

    await updateTask({ id: currentTask.id, subtasks: updatedSubtasks });
  };

  const handleDeleteSubtask = async (index: number) => {
    const currentSubtasks = currentTask.subtasks || [];

    // Filter out the deleted subtask and sanitize the rest
    const updatedSubtasks = currentSubtasks
      .filter((_, i) => i !== index)
      .map(st => ({
        title: st.title,
        completed: st.completed
      }));

    await updateTask({ id: currentTask.id, subtasks: updatedSubtasks });
  };

  const handleLogTime = async () => {
    if (!timeEntryHours || !user) return;
    const hours = parseFloat(timeEntryHours);
    if (isNaN(hours) || hours <= 0) return;

    const currentEntries = currentTask.timeEntries || [];
    await updateTask({
      id: currentTask.id,
      timeEntries: [
        ...currentEntries,
        {
          userId: user.id,
          hours,
          description: timeEntryDesc,
          date: new Date().toISOString(),
        },
      ],
    });
    setTimeEntryHours('');
    setTimeEntryDesc('');
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    await createComment({
      projectId,
      taskId: currentTask.id,
      text: newComment,
    });
    await fetchComments(projectId);
    setNewComment('');
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      // 1. Upload file to project
      const projectFile = await uploadFile(projectId, file);

      if (projectFile) {
        // 2. Link file to task
        const currentAttachments = currentTask.attachments || [];
        const newAttachment: TaskAttachment = {
          assetId: projectFile.id, // Using project file ID as asset ID reference
          fileName: projectFile.fileName,
          fileUrl: projectFile.fileUrl,
          fileType: projectFile.mimeType || '',
          fileSize: projectFile.fileSize || 0,
        };

        await updateTask({
          id: currentTask.id,
          attachments: [...currentAttachments, newAttachment],
        });
      }
    } catch (error) {
      console.error('Error uploading file:', error);
    }

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleLinkAsset = async (assetId: string, asset: any) => {
    const currentAttachments = currentTask.attachments || [];
    // Don't send attachedBy and attachedAt - backend will set these
    const newAttachment: TaskAttachment = {
      assetId,
      fileName: asset.originalName || asset.name || 'asset',
      fileUrl: asset.cdnUrl || asset.url || '',
      fileType: asset.mimeType || asset.type || '',
      fileSize: asset.fileSize || 0,
      // attachedBy and attachedAt are optional and will be set by backend
    };
    await updateTask({
      id: currentTask.id,
      attachments: [
        ...currentAttachments,
        newAttachment,
      ],
    });
    setShowAssetPicker(false);
  };

  const totalLoggedHours = currentTask.timeEntries?.reduce((sum, entry) => sum + (entry.hours || 0), 0) || 0;
  const completedSubtasks = currentTask.subtasks?.filter(st => st.completed).length || 0;
  const totalSubtasks = currentTask.subtasks?.length || 0;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        {/* Backdrop */}
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

        {/* Modal */}
        <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex-1 min-w-0">
              {isEditing ? (
                <input
                  type="text"
                  value={editedTitle}
                  onChange={(e) => setEditedTitle(e.target.value)}
                  className="text-xl font-semibold text-gray-900 dark:text-white w-full px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
                  autoFocus
                />
              ) : (
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white truncate">{currentTask.title}</h2>
              )}
            </div>
            <div className="flex items-center gap-2 ml-4">
              <button
                onClick={() => isEditing ? handleSave() : setIsEditing(true)}
                className="p-2 text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/30 rounded-lg transition-colors"
              >
                <Edit className="w-5 h-5" />
              </button>
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {/* Status and Priority */}
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Status:</span>
                <div className="flex gap-2 flex-wrap">
                  {(['todo', 'in_progress', 'in_review', 'done'] as Task['status'][]).map(status => (
                    <button
                      key={status}
                      onClick={() => handleStatusChange(status)}
                      className={`px-3 py-1.5 rounded-xl text-sm font-medium transition-colors ${currentTask.status === status
                        ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 border-2 border-primary-500'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 border-2 border-transparent'
                        }`}
                    >
                      {getStatusIcon(status)}
                      <span className="ml-1 capitalize">{status.replace('_', ' ')}</span>
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Priority:</span>
                <div className="flex gap-1">
                  {(['low', 'medium', 'high', 'urgent'] as Task['priority'][]).map(priority => (
                    <button
                      key={priority}
                      onClick={() => handlePriorityChange(priority)}
                      className={`p-1.5 rounded-lg transition-opacity ${currentTask.priority === priority ? 'opacity-100 bg-gray-100 dark:bg-gray-700' : 'opacity-40 hover:opacity-70'}`}
                    >
                      {getPriorityIcon(priority)}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Assigned To and Due Date */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center gap-2 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                <User className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                <span className="text-sm text-gray-600 dark:text-gray-400">Assigned to:</span>
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {getAssignedUserName(currentTask.assignedTo)}
                </span>
                <button
                  onClick={() => setShowAssignmentModal(true)}
                  className="ml-auto px-2 py-1 text-xs bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 rounded-lg hover:bg-primary-200 dark:hover:bg-primary-900/50 transition-colors"
                >
                  Change
                </button>
              </div>
              <div className="flex items-center gap-2 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                <Calendar className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                <span className="text-sm text-gray-600 dark:text-gray-400">Due Date:</span>
                {isEditing ? (
                  <input
                    type="date"
                    value={editedDueDate ? new Date(editedDueDate).toISOString().split('T')[0] : ''}
                    onChange={(e) => setEditedDueDate(e.target.value)}
                    className="px-3 py-1.5 text-sm bg-white dark:bg-gray-600 border border-gray-200 dark:border-gray-500 rounded-lg text-gray-900 dark:text-white"
                  />
                ) : (
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    {currentTask.dueDate ? formatDate(currentTask.dueDate) : 'No due date'}
                  </span>
                )}
              </div>
            </div>

            {/* Description */}
            <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Description</h3>
              {isEditing ? (
                <textarea
                  value={editedDescription}
                  onChange={(e) => setEditedDescription(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 resize-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
                  rows={4}
                />
              ) : (
                <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                  {currentTask.description || 'No description'}
                </p>
              )}
            </div>

            {/* Attachments */}
            <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Attachments</h3>
                <div className="flex gap-2">
                  <input
                    type="file"
                    ref={fileInputRef}
                    className="hidden"
                    onChange={handleFileUpload}
                  />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="flex items-center gap-1 px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  >
                    <Upload className="w-4 h-4" />
                    Upload
                  </button>
                  <button
                    onClick={() => setShowAssetPicker(true)}
                    className="flex items-center gap-1 px-3 py-1.5 text-sm bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 rounded-xl hover:bg-primary-200 dark:hover:bg-primary-900/50 transition-colors"
                  >
                    <LinkIcon className="w-4 h-4" />
                    Link from Asset Library
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                {currentTask.attachments && currentTask.attachments.length > 0 ? (
                  currentTask.attachments.map((attachment, idx) => (
                    <div key={idx} className="flex items-center gap-2 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                      <FileText className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                      <span className="text-sm text-gray-700 dark:text-gray-300 flex-1">{attachment.fileName}</span>
                      {attachment.assetId && (
                        <span className="text-xs text-primary-600 dark:text-primary-400">(from Asset Library)</span>
                      )}
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-gray-500 dark:text-gray-400">No attachments</p>
                )}
              </div>
            </div>

            {/* Subtasks */}
            <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                  Subtasks ({completedSubtasks}/{totalSubtasks} completed)
                </h3>
              </div>
              <div className="space-y-2 mb-3">
                {currentTask.subtasks && currentTask.subtasks.length > 0 ? (
                  currentTask.subtasks.map((subtask, idx) => (
                    <div key={idx} className="flex items-center gap-2 group p-2 hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded-lg transition-colors">
                      <button
                        onClick={() => handleToggleSubtask(idx)}
                        className="flex-shrink-0"
                      >
                        {subtask.completed ? (
                          <CheckCircle className="w-5 h-5 text-green-500" />
                        ) : (
                          <Circle className="w-5 h-5 text-gray-400 dark:text-gray-500" />
                        )}
                      </button>
                      <span className={`text-sm flex-1 ${subtask.completed ? 'line-through text-gray-500 dark:text-gray-400' : 'text-gray-900 dark:text-white'}`}>
                        {subtask.title}
                      </span>
                      <button
                        onClick={() => handleDeleteSubtask(idx)}
                        className="p-1 text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-gray-500 dark:text-gray-400">No subtasks</p>
                )}
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newSubtask}
                  onChange={(e) => setNewSubtask(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddSubtask();
                    }
                  }}
                  placeholder="Add subtask..."
                  className="flex-1 px-4 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
                />
                <button
                  onClick={handleAddSubtask}
                  className="px-4 py-2 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors text-sm"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Time Tracking */}
            <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Time Tracking</h3>
              <div className="grid grid-cols-3 gap-4 mb-3">
                <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl text-center">
                  <span className="text-xs text-gray-500 dark:text-gray-400">Estimated</span>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{currentTask.estimatedHours || 0}h</p>
                </div>
                <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl text-center">
                  <span className="text-xs text-gray-500 dark:text-gray-400">Logged</span>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{totalLoggedHours.toFixed(1)}h</p>
                </div>
                <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl text-center">
                  <span className="text-xs text-gray-500 dark:text-gray-400">Remaining</span>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {Math.max(0, (currentTask.estimatedHours || 0) - totalLoggedHours).toFixed(1)}h
                  </p>
                </div>
              </div>
              <div className="flex gap-2 mb-2">
                <input
                  type="number"
                  step="0.5"
                  value={timeEntryHours}
                  onChange={(e) => setTimeEntryHours(e.target.value)}
                  placeholder="Hours"
                  className="w-24 px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
                />
                <input
                  type="text"
                  value={timeEntryDesc}
                  onChange={(e) => setTimeEntryDesc(e.target.value)}
                  placeholder="Description (optional)"
                  className="flex-1 px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
                />
                <button
                  onClick={handleLogTime}
                  className="px-4 py-2 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors text-sm"
                >
                  Log Time
                </button>
              </div>
              <button className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors text-sm">
                <Play className="w-4 h-4" />
                Start Timer
              </button>
            </div>

            {/* Comments */}
            <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Comments</h3>
              <div className="space-y-3 mb-3 max-h-60 overflow-y-auto">
                {comments.filter(c => c.taskId === currentTask.id).length > 0 ? (
                  comments
                    .filter(c => c.taskId === currentTask.id)
                    .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
                    .map((comment) => (
                      <div key={comment.id} className="bg-gray-50 dark:bg-gray-700/50 p-3 rounded-xl">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-medium text-gray-900 dark:text-white">
                            {getAssignedUserName(comment.author)}
                          </span>
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {new Date(comment.createdAt).toLocaleString()}
                          </span>
                        </div>
                        <p className="text-sm text-gray-700 dark:text-gray-300">{comment.text}</p>
                      </div>
                    ))
                ) : (
                  <p className="text-sm text-gray-500 dark:text-gray-400">No comments yet</p>
                )}
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleAddComment();
                    }
                  }}
                  placeholder="Add a comment..."
                  className="flex-1 px-4 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
                />
                <button
                  onClick={handleAddComment}
                  className="px-4 py-2 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors text-sm"
                >
                  <MessageSquare className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
            <button
              onClick={async () => {
                if (confirm('Are you sure you want to delete this task?')) {
                  await deleteTask(currentTask.id);
                  onClose();
                }
              }}
              className="px-4 py-2.5 text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded-xl hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
            >
              Delete Task
            </button>
            <button
              onClick={async () => {
                await updateTask({ id: currentTask.id, status: 'done' });
                onClose();
              }}
              className="px-6 py-2.5 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl hover:from-green-700 hover:to-emerald-700 transition-all shadow-lg hover:shadow-xl"
            >
              Mark as Complete
            </button>
          </div>
        </div>

        {/* Task Assignment Modal */}
        {showAssignmentModal && (
          <TaskAssignmentModal
            isOpen={showAssignmentModal}
            onClose={() => setShowAssignmentModal(false)}
            task={currentTask}
          />
        )}

        {/* Asset Library Picker */}
        {showAssetPicker && (
          <AssetLibraryPicker
            isOpen={showAssetPicker}
            onClose={() => setShowAssetPicker(false)}
            onSelect={handleLinkAsset}
          />
        )}
      </div>
    </div>
  );
};

export default TaskDetailModal;

